// src/components/Dashboard/RoleDashboard/RoleDashboard.js
import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../apiFetch";
import RoleDashboardLayout from "./RoleDashboardLayout";

import {
    Routes,
    Route,
    useLocation,
    useNavigate,
    Navigate,
} from "react-router-dom";

import "./RoleDashboard.css";
import getComponentsForRole from "./componentsRegistry";
import ROLE_CONFIG from "./roleConfig";
import getRoutesForRole from "./routesRegistry";
import Header from "./Header"; // ✅ shared role-aware header

const isDev =
    typeof process !== "undefined" && process.env.NODE_ENV !== "production";

export default function RoleDashboard({ role: roleProp, initialSection }) {
    const location = useLocation();
    const navigate = useNavigate();




    // Normalize role from prop or storage
    const role = useMemo(() => {
        const r =
            (
                roleProp ||
                localStorage.getItem("userRole") ||
                localStorage.getItem("role") ||
                ""
            ).toLowerCase();
        return ["agent", "scout", "club"].includes(r) ? r : "";
    }, [roleProp]);

    // Which localStorage key holds the entity id for this role
    const idKey = useMemo(
        () => ({ agent: "agentId", scout: "scoutId", club: "clubId" })[role],
        [role]
    );

    // Redirect to CompleteRegistration unless BOTH role & role-id exist
    useEffect(() => {
        if (!role) return; // wait until role is known

        const id = idKey && localStorage.getItem(idKey);
        if (!id) navigate("/complete-registration", { replace: true });
    }, [role, idKey, navigate]);


    const C = useMemo(() => getComponentsForRole(role || "agent"), [role]);
    const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.agent;

    const [profileImage, setProfileImage] = useState(null);
    const [userId, setUserId] = useState(null);
    const [savedOpportunities, setSavedOpportunities] = useState([]);
    const [activeConversationId, setActiveConversationId] = useState(null);
    const [showInbox, setShowInbox] = useState(false);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [checkingSubscription, setCheckingSubscription] = useState(true);
    const [betaAccess, setBetaAccess] = useState(null);
    const [checkingMe, setCheckingMe] = useState(true);




    // ✅ Base path is role-specific: /agent-dashboard, /scout-dashboard, /club-dashboard
    const profileRouteBase = useMemo(() => `/${role}-dashboard`, [role]);

    // ✅ Detect VSN Transfers sub-pages (contract-offer / manage-contract / show-interest / offer-to-sign/:playerId)
    const isTransferPage = useMemo(() => {
        const path = (location.pathname || "").replace(/\/+$/, "");

        if (path.includes("/offer-to-sign/")) return true;

        return (
            path.endsWith("/contract-offer") ||
            path.endsWith("/manage-contract") ||
            path.endsWith("/show-interest") ||
            path.endsWith("/report") ||
            path.endsWith("/vsn-world") ||
            path.endsWith("/chats")          // ✅ THIS IS THE FIX
        );
    }, [location.pathname]);


    // ✅ Detect Reporting page (full-width layout)
    const isReportingPage = useMemo(() => {
        const path = (location.pathname || "").replace(/\/+$/, "");
        return path.endsWith("/report");
    }, [location.pathname]);



    // ✅ Detect Video Profile fullscreen
    const isVideoProfileFullscreen = useMemo(() => {
        const path = (location.pathname || "").replace(/\/+$/, "");
        return path.endsWith("/video-profiles");
    }, [location.pathname]);


    const openWhatsApp = () => {
        const phone = "254729999145"; // without +
        const message = encodeURIComponent(
            "Hi Megavolto? I want Early Bird Beta Access for Virtual Scouting Network. Please activate my account."
        );

        window.open(`https://wa.me/${phone}?text=${message}`, "_blank");
    };


    // ✅ Detect slug video profile page: /<role>-dashboard/video-profile/:profileSlug
    const isVideoProfilePage = useMemo(() => {
        const path = (location.pathname || "").replace(/\/+$/, "");
        return path.includes(`/${role}-dashboard/video-profile/`);
    }, [location.pathname, role]);



    // ✅ Detect PlayerProfile page (so dashboard background becomes black)
    const isPlayerProfilePage = useMemo(() => {
        const path = (location.pathname || "").replace(/\/+$/, "");
        return path.includes(`/${role}-dashboard/player-profile/`);
    }, [location.pathname, role]);









    // ✅ Detect if we are on the DASHBOARD ROOT only
    const isDashboardRoot = useMemo(() => {
        const path = (location.pathname || "").replace(/\/+$/, "");

        if (path !== `/${role}-dashboard`) return false;
        if (isTransferPage || isVideoProfileFullscreen) return false;

        return true;
    }, [location.pathname, role, isTransferPage, isVideoProfileFullscreen]);



    useEffect(() => {
        console.log("🧱 RoleDashboard MOUNTED");
        return () => console.log("🧹 RoleDashboard UNMOUNTED");
    }, []);




    useEffect(() => {
        const fetchMe = async () => {
            try {
                const res = await apiFetch("/me");
                if (res.ok) {
                    const data = await res.json();
                    const normalizeBool = (v) =>
                        v === true || v === "true" || v === 1 || v === "1";

                    setBetaAccess(normalizeBool(data?.betaAccess));
                }
            } finally {
                setCheckingMe(false);
            }
        };

        fetchMe();
    }, []);




    // Keep online status up to date
    useEffect(() => {
        if (!userId) return;

        const updateStatus = async () => {
            try {
                await apiFetch("/api/online-status/update", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ userId }),
                });
            } catch (err) {
                console.error("❌ Error updating online status:", err);
            }
        };




        updateStatus();
        const interval = setInterval(updateStatus, 60000);
        return () => clearInterval(interval);
    }, [userId]);


    // Load userId; pick up conversation state (🚫 do NOT write role keys here)
    useEffect(() => {
        if (idKey) {
            const storedId = localStorage.getItem(idKey);
            if (storedId) setUserId(storedId);
        }

        if (location.state?.openConversationId) {
            setActiveConversationId(location.state.openConversationId);
            if (location.state.openFullChat) setShowInbox(true);

            // ✅ DO NOT clear here.
            // RoleChats needs this state to auto-open the conversation.
            // We'll clear it inside RoleChats after it consumes it.
        }
    }, [idKey, location.state]);


    useEffect(() => {
        if (!userId) return;

        const checkSubscription = async () => {
            try {
                const res = await apiFetch("/api/subscription/status");

                if (!res.ok) throw new Error("Subscription status check failed");

                const data = await res.json();
                setSubscriptionStatus(data);


            } catch (err) {
                console.error("❌ Subscription check failed:", err);
            } finally {
                setCheckingSubscription(false);
            }
        };

        checkSubscription();
    }, [userId]);



    useEffect(() => {
        if (!role || !userId) return;

        const fetchProfileImage = async () => {
            try {
                const res = await apiFetch(`/api/${role}/${userId}/profile`);

                if (!res.ok) return;

                const data = await res.json();


                const img =
                    data.profileImageUrl ||
                    data.avatarUrl ||
                    data.avatar ||
                    null;

                if (img) {
                    setProfileImage(img);
                }
            } catch (err) {
                console.error("❌ Failed to hydrate profile image:", err);
            }
        };

        fetchProfileImage();
    }, [role, userId]);


    // If we arrived via pretty URL like /ScoutDashboard/Profile, jump to that section
    const initialPath = useMemo(
        () => (initialSection ? String(initialSection).toLowerCase() : ""),
        [initialSection]
    );

    // Role-aware context passed to all widgets (keeps legacy Agent props working)
    const roleCtx = useMemo(
        () => ({
            role,
            userId,
            id: userId, // neutral id
            agentId: role === "agent" ? userId : undefined,
            scoutId: role === "scout" ? userId : undefined,
            clubId: role === "club" ? userId : undefined,
            permissions: cfg.permissions,
            limits: cfg.limits,
            pricing: cfg.pricing,
        }),
        [role, userId, cfg]
    );

    // Extra pages (from routesRegistry). Inject roleCtx into each element.
    const extraRoutes = useMemo(() => {
        const list = getRoutesForRole(role || "agent");
        return list.map((r) => ({
            ...r,
            element: React.cloneElement(r.element, roleCtx),
        }));
    }, [role, roleCtx]);




    const handleSubscribe = async () => {
        try {
            const res = await apiFetch("/api/checkout/subscription", {
                method: "POST",
            });

            if (!res.ok) throw new Error("Subscription checkout failed");

            const data = await res.json();


            if (data.url) {
                window.location.href = data.url;
            }
        } catch (err) {
            console.error("❌ Subscription checkout failed:", err);
        }
    };






    const handleLogout = () => {
        [
            "agentId",
            "scoutId",
            "clubId",
            "playerId",
            "userRole",
            "role",
            "playerProfileSlug",
            "isDisabled",
            "authUid",
        ].forEach((k) => localStorage.removeItem(k));

        window.location.href = "/login";
    };


    const handleProfileImageChange = (img) => setProfileImage(img);

    const handlePostOpportunity = (
        compiledOpportunity,
        filterOption,
        filterValue
    ) => {
        setSavedOpportunities((prev) => [
            ...prev,
            { compiledOpportunity, filterOption, filterValue },
        ]);
    };




    const dashboardGrid = useMemo(() => {
        // ✅ Keep hook order stable, but don't render grid until ready
        if (!userId || checkingSubscription) return null;

        return (
            <div className="grid-container">
                {/* LEFT */}
                <div className="left-section">
                    <div className="card">
                        <C.Profile
                            {...roleCtx}
                            onProfileImageChange={handleProfileImageChange}
                            basePath={profileRouteBase}
                        />
                    </div>
                    <div className="card">
                        <C.Inbox
                            {...roleCtx}
                            openConversationId={activeConversationId}
                            forceOpen={showInbox}
                        />
                    </div>
                </div>

                {/* CENTER */}
                <div className="center-section">
                    <C.PostOpportunity
                        {...roleCtx}
                        onPostOpportunity={handlePostOpportunity}
                        source={role.charAt(0).toUpperCase() + role.slice(1)}
                        priceUSD={cfg.pricing.playerPurchaseUSD}
                    />

                    <div className="video-profile-section">
                        <div className="card large-card">
                            <C.VideoProfile
                                {...roleCtx}
                                className="large-video-profile"
                                viewerId={userId}
                                viewerRole={role}
                            />
                        </div>
                    </div>
                </div>

                {/* RIGHT */}
                <div className="right-section">
                    <div className="card">
                        <C.SavedOpportunity
                            {...roleCtx}
                            opportunities={savedOpportunities}
                        />
                    </div>
                    <div className="card">
                        <C.Transfers {...roleCtx} />
                    </div>
                </div>
            </div>
        );
    }, [
        C,
        roleCtx,
        profileRouteBase,
        activeConversationId,
        showInbox,
        savedOpportunities,
        role,
        userId,
        checkingSubscription, // ✅ add this
        cfg.pricing.playerPurchaseUSD,
    ]);



    // Early return AFTER all hooks are declared
    if (!userId || checkingSubscription || checkingMe) {
        return <div>Loading...</div>;
    }


    const hasPaidAccess = ["active", "trialing"].includes(subscriptionStatus?.status);
    const requiresSub = !!subscriptionStatus?.required;

    // user is NOT approved for beta
    const isBetaLocked = betaAccess === false;

    // beta-approved users bypass payment
    const bypassSubscriptionForBeta = betaAccess === true;

    // subscription lock only applies to non-beta-approved users
    const isSubscriptionLocked =
        !isBetaLocked &&
        requiresSub &&
        !hasPaidAccess &&
        !bypassSubscriptionForBeta;



    const NotFound = () => (
        <div className="p-8 text-center text-sm text-gray-500">
            Section not found.
        </div>
    );

    if (isDev) {
        console.debug("[RoleDashboard] mount", {
            role,
            userId,
            profileRouteBase,
            initialPath,
            location: location.pathname,
            isTransferPage,
        });
    }

    return (
        <>
            <div
                className={`role-dashboard ${role}-dashboard
                ${isSubscriptionLocked || isBetaLocked ? "dashboard-blurred" : ""}
                ${isTransferPage ? "transfer-page-wrapper" : ""}
                ${isVideoProfileFullscreen ? "video-profile-fullscreen" : ""}
                ${isVideoProfilePage ? "video-profile-page" : ""}
                ${isReportingPage ? "reporting-page" : ""}
                ${isPlayerProfilePage ? "player-profile-page" : ""}
               `}
            >



                {isVideoProfileFullscreen && (
                    <button
                        className="back-to-dashboard-btn"
                        onClick={() => navigate(`/${role}-dashboard`)}
                        aria-label="Back to Dashboard"
                    >
                        ← Back to Dashboard
                    </button>
                )}



                {isDashboardRoot &&
                    !isVideoProfileFullscreen &&
                    !isSubscriptionLocked &&
                    !isBetaLocked && (
                        <Header
                            profileImage={profileImage}
                            onLogout={handleLogout}
                            userId={userId}
                            role={role}
                            title={cfg.title}
                        />
                    )}



                <main className="main-content">




                    <Routes>
                        {/* ✅ Layout keeps DashboardGrid (and VideoProfile) mounted */}
                        <Route
                            element={
                                <RoleDashboardLayout
                                    dashboardGrid={dashboardGrid}
                                />

                            }
                        >

                            {/* Dashboard home */}
                            {initialPath ? (
                                <Route index element={<Navigate to={initialPath} replace />} />
                            ) : (
                                <Route index element={null} />
                            )}

                            {/* ✅ Fullscreen URL still works without unmounting VideoProfile */}
                            <Route path="video-profiles" element={null} />



                            {/* ✅ All child pages render via <Outlet /> without unmounting DashboardGrid */}
                            {extraRoutes.map((r) => (
                                <Route key={r.path} path={r.path} element={r.element} />
                            ))}

                            <Route path="*" element={<NotFound />} />
                        </Route>
                    </Routes>

                </main>

                {/* ✅ FOOTER (ONLY ON MAIN DASHBOARD) */}
                {isDashboardRoot && (
                    <footer className="dashboard-footer">
                        <p>
                            © {new Date().getFullYear()} Virtual Scouting Network
                        </p>

                        <div className="dashboard-footer-links">
                            <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer">
                                Privacy Policy
                            </a>
                            <span>·</span>
                            <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer">
                                Terms of Service
                            </a>
                        </div>
                    </footer>
                )}
            </div>



            {isBetaLocked && (
                <div className="dashboard-overlay">
                    <div
                        className="dashboard-overlay-content"
                        style={{
                            backgroundColor: "#0b0b0b",
                            border: "2px solid #FFD600",
                            color: "#fff",
                            padding: "28px",
                            borderRadius: "12px",
                            width: "90%",
                            maxWidth: "420px",
                            textAlign: "center",
                            boxShadow: "0px 10px 40px rgba(0,0,0,0.8)",
                        }}
                    >
                        <h2 style={{ color: "#FFD600", marginBottom: "10px" }}>
                            🚧 Early Bird Beta Access
                        </h2>

                        <p style={{ color: "#ddd", lineHeight: "1.6", fontSize: "14px" }}>
                            Virtual Scouting Network is currently in{" "}
                            <strong style={{ color: "#FFD600" }}>Beta Testing Mode</strong>.
                            <br />
                            <br />
                            We’re still working to resolve payment & subscription options.
                        </p>

                        <p
                            style={{
                                marginTop: "15px",
                                color: "#FFD600",
                                fontWeight: "bold",
                                fontSize: "14px",
                            }}
                        >
                            To get access now, message us on WhatsApp and we’ll activate your account manually.
                        </p>

                        <button
                            onClick={openWhatsApp}
                            style={{
                                marginTop: "18px",
                                backgroundColor: "#25D366",
                                color: "#000",
                                fontWeight: "bold",
                                border: "none",
                                padding: "14px 18px",
                                borderRadius: "8px",
                                cursor: "pointer",
                                width: "100%",
                                fontSize: "15px",
                            }}
                        >
                            💬 Request Beta Access
                        </button>

                        <p
                            style={{
                                marginTop: "12px",
                                fontSize: "12px",
                                color: "#aaa",
                            }}
                        >
                            WhatsApp: <span style={{ color: "#fff" }}>+254 729 999 145</span>
                        </p>
                    </div>
                </div>
            )}




            {isSubscriptionLocked && (
                <div className="dashboard-overlay">
                    <div className="dashboard-overlay-content">
                        <h2>Unlock Virtual Scouting Network</h2>
                        <p>
                            Subscribe to Virtual Scouting Network to scout football players worldwide, watch exclusive player videos, and access professional scouting and messaging tools.
                        </p>
                        <button onClick={handleSubscribe}>
                            Continue to Payment
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
